import { memo } from 'react';
import { Avatar, Badge, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { GROUP_CARD_MAX_AVATARS } from '../../constants/groups';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { initialsOf } from '../../utils/groupPaths';

/**
 * Overlapping member avatars (initials) with a `+N` overflow chip. The hidden members that the
 * API previewed are listed in the chip's tooltip, the rest as "and N more".
 *
 * @param {object} props
 * @param {{ id: string, name: string }[]} props.members Previewed members, in display order.
 * @param {number} props.total Member count of the group.
 * @param {number} [props.max] Avatars shown before the overflow chip.
 */
const MemberAvatarStack = ({ members, total, max = GROUP_CARD_MAX_AVATARS }) => {
  const { t } = useTranslation('people-access');

  if (total === 0 || members.length === 0) return null;

  const visible = members.slice(0, max);
  const hidden = members.slice(max);
  const overflow = total - visible.length;
  const unknown = overflow - hidden.length;
  const tooltip = [
    ...hidden.map((member) => member.name),
    ...(unknown > 0 ? [t('groups.card.andMore', { count: unknown })] : []),
  ].join(', ');

  return (
    <div className="flex items-center">
      <div className="flex -space-x-1.5">
        {visible.map((member) => (
          <Avatar
            key={member.id}
            size="md"
            initials={initialsOf(member.name)}
            alt={member.name}
            className={`${getAvatarColorClass(member.name)} text-[10px] ring-2 ring-surface`}
          />
        ))}
      </div>
      {overflow > 0 && (
        <Tooltip content={tooltip}>
          <span className="ml-1.5 inline-flex" aria-label={t('groups.card.moreAria', { count: overflow })}>
            <Badge variant="neutral">{`+${overflow}`}</Badge>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(MemberAvatarStack);
